/**
 * SOLOMON — Generated-file download card
 *
 * Rendered under an assistant bubble when the response carries
 * generatedFiles (the generate_file agentic tool). Ephemeral v1: cards live
 * in the current session only and files expire on a 30-min server TTL — an
 * expired download shows a friendly regenerate hint, never a raw 404.
 */

import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function humanSize(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileCard({ file }) {
  const { authFetch } = useAuth();
  const [state, setState] = useState('idle'); // idle | downloading | expired | error

  const handleDownload = async () => {
    if (state === 'downloading') return;
    setState('downloading');
    try {
      const res = await authFetch(`${API_URL}/api/files/${file.fileId}`);
      if (res.status === 404) { setState('expired'); return; }
      if (!res.ok) { setState('error'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setState('idle');
    } catch {
      setState('error');
    }
  };

  return (
    <div className="file-card">
      <span className="file-card-icon" aria-hidden="true">
        {file.type === 'xlsx' ? <SpreadsheetIcon /> : <DocumentIcon />}
      </span>
      <span className="file-card-info">
        <span className="file-card-name" title={file.name}>{file.name}</span>
        <span className="file-card-meta">
          {file.type?.toUpperCase()}{file.size ? ` · ${humanSize(file.size)}` : ''}
        </span>
      </span>

      {state === 'expired' ? (
        <span className="file-card-expired">This file expired — ask Solomon to regenerate it.</span>
      ) : state === 'error' ? (
        <span className="file-card-expired">Download failed — try again.</span>
      ) : null}

      {state !== 'expired' && (
        <button
          type="button"
          className="file-card-download"
          onClick={handleDownload}
          disabled={state === 'downloading'}
          title={`Download ${file.name}`}
          aria-label={`Download ${file.name}`}
        >
          <DownloadIcon />
          <span>{state === 'downloading' ? 'Preparing…' : 'Download'}</span>
        </button>
      )}
    </div>
  );
}

// Inline thin-line SVGs — house pattern (no icon dependency)

function DocumentIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="14" y2="17" />
    </svg>
  );
}

function SpreadsheetIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
